import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Drawer,
  Empty,
  Input,
  Pagination,
  Segmented,
  Select,
  Spin,
  Tag,
  message,
} from "antd";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";
import ReadOnlyDocumentViewer from "../../components/Document/ReadOnlyDocumentViewer";
import {
  fetchArticleBankEntries,
  fetchArticleBankEntry,
  fetchArticleBankFacets,
} from "../../service/articleBank";
import {
  DetailHeading,
  DetailInfo,
  DetailLayout,
  DetailSection,
  DetailValue,
  EmptyState,
  FilterFooter,
  Header,
  InlinePreviewPanel,
  Meta,
  Page,
  PaginationRow,
  PreviewBody,
  ResultCard,
  ResultList,
  ResultSummary,
  ResultTitle,
  ResultTop,
  SearchPanel,
  SearchRow,
  Snippet,
  Subtitle,
  TagRow,
  Title,
} from "../BriefBank/BriefBankStyled";
import {
  BrowseHeading,
  BrowsePanel,
  PartRow,
  Quote,
  QuotePage,
  ShelfNote,
  ShelfRow,
  ShelfSubheading,
  SupportExplanation,
  SupportItem,
  SupportLabel,
} from "./ArticlesBankStyled";

const PAGE_SIZE = 25;
const STRENGTH_COLORS = { strong: "green", moderate: "gold", weak: "default" };
const VISIBLE_CONDITIONS = 5;
const VISIBLE_SHELF_CONDITIONS = 24;
const PART_COLORS = { nexus: "green", evaluation: "orange", both: "geekblue" };
const PART_TAGS = { nexus: "Nexus", evaluation: "Evaluation", both: "Nexus + Evaluation" };
const PART_FILTER_LABELS = { nexus: "Nexus", evaluation: "Evaluation", both: "Both" };
const SHELF_TABS = [
  { value: "condition", label: "Medical conditions" },
  { value: "exposure", label: "Toxic exposures" },
  { value: "event", label: "Service events" },
];
const SHELF_NOTES = {
  condition: "Pick a body system, then a condition if you want to narrow it down.",
  exposure: "Agent Orange, burn pits, contaminated water and other toxic exposures.",
  event: "Service events that are not toxic exposures, such as combat or military sexual trauma.",
};

const responseData = (response) => response?.data || response || {};

const apiError = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.detail ||
  error?.message ||
  fallback;

const highlightedSnippet = (snippet) => {
  const parts = String(snippet || "").split(/(\[\[HIT\]\]|\[\[\/HIT\]\])/);
  let highlighted = false;
  return parts.map((part, index) => {
    if (part === "[[HIT]]") {
      highlighted = true;
      return null;
    }
    if (part === "[[/HIT]]") {
      highlighted = false;
      return null;
    }
    return highlighted ? <mark key={`${part}-${index}`}>{part}</mark> : part;
  });
};

const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const facetOptions = (items = []) =>
  items.map((item) => ({ value: item.value, label: `${item.label} (${item.count})` }));

const ArticlesBank = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const conditions = searchParams.getAll("condition");
  const bodySystems = searchParams.getAll("body_system");
  const exposures = searchParams.getAll("exposure");
  const events = searchParams.getAll("event");
  const claimParts = searchParams.getAll("claim_part");
  const page = Number(searchParams.get("page") || 1);
  const paramsKey = searchParams.toString();

  const [queryInput, setQueryInput] = useState(query);
  const [shelfTab, setShelfTab] = useState(
    exposures.length ? "exposure" : events.length ? "event" : "condition"
  );
  const [showAllConditions, setShowAllConditions] = useState(false);
  const [facets, setFacets] = useState({});
  const [facetLoading, setFacetLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchError, setSearchError] = useState("");
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailRequestRef = useRef(0);

  useEffect(() => setQueryInput(query), [query]);

  useEffect(() => {
    let cancelled = false;
    fetchArticleBankFacets()
      .then((response) => {
        if (!cancelled) setFacets(responseData(response));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setFacetLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSearchError("");
    fetchArticleBankEntries({
      page,
      pageSize: PAGE_SIZE,
      query,
      conditions,
      bodySystems,
      exposures,
      events,
      claimParts,
    })
      .then((response) => {
        if (cancelled) return;
        const data = responseData(response);
        setEntries(data.results || []);
        setTotal(data.count || 0);
      })
      .catch((error) => {
        if (cancelled) return;
        setEntries([]);
        setTotal(0);
        setSearchError(apiError(error, "Articles could not load."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // paramsKey stands in for every URL-backed filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const updateParams = (mutate) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    next.set("page", "1");
    setSearchParams(next);
  };

  const setMany = (key, values) =>
    updateParams((next) => {
      next.delete(key);
      values.forEach((value) => next.append(key, value));
    });

  const openDetail = async (entry) => {
    const requestId = ++detailRequestRef.current;
    setSelected(entry);
    setDetailLoading(true);
    try {
      const response = await fetchArticleBankEntry(entry.id);
      if (requestId === detailRequestRef.current) setSelected(responseData(response));
    } catch (error) {
      if (requestId === detailRequestRef.current) {
        message.error(apiError(error, "Article details could not load."));
      }
    } finally {
      if (requestId === detailRequestRef.current) setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    detailRequestRef.current += 1;
    setSelected(null);
    setDetailLoading(false);
  };

  const conditionOptions = useMemo(() => facetOptions(facets.conditions), [facets]);
  const bodySystemFacets = useMemo(() => facets.body_systems || [], [facets]);
  const exposureFacets = facets.exposures || [];
  const eventFacets = facets.events || [];
  const claimPartFacets = facets.claim_parts || [];
  const partOptions = useMemo(
    () => [
      { value: "any", label: "Any" },
      ...(facets.claim_parts || []).map((part) => ({
        value: part.value,
        label: `${PART_FILTER_LABELS[part.value] || part.label} (${part.count})`,
      })),
    ],
    [facets]
  );
  const shelfFacets =
    shelfTab === "exposure" ? exposureFacets : shelfTab === "event" ? eventFacets : bodySystemFacets;
  const shelfKey =
    shelfTab === "exposure" ? "exposure" : shelfTab === "event" ? "event" : "body_system";
  const shelfSelected =
    shelfTab === "exposure" ? exposures : shelfTab === "event" ? events : bodySystems;
  const openSystemConditions = bodySystemFacets
    .filter((system) => bodySystems.includes(system.value))
    .map((system) => ({
      ...system,
      conditions: showAllConditions
        ? system.conditions
        : system.conditions.slice(0, VISIBLE_SHELF_CONDITIONS),
      hidden: Math.max(system.conditions.length - VISIBLE_SHELF_CONDITIONS, 0),
    }));
  const toggleShelf = (key, value, checked) => {
    const current = searchParams.getAll(key);
    const nextValues = checked ? [...current, value] : current.filter((v) => v !== value);
    updateParams((next) => {
      next.delete(key);
      nextValues.forEach((v) => next.append(key, v));
      if (key === "body_system" && !checked) {
        // Leaving a body system also drops the conditions chosen under it.
        const dropped = new Set(
          (bodySystemFacets.find((system) => system.value === value)?.conditions || []).map(
            (condition) => condition.value
          )
        );
        const keep = next.getAll("condition").filter((c) => !dropped.has(c));
        next.delete("condition");
        keep.forEach((c) => next.append("condition", c));
      }
    });
  };

  const canPreview = Boolean(selected?.source_document_id && selected?.claim_support);
  const sourceType = String(selected?.source_name || "").split(".").pop().toLowerCase();

  return (
    <Page>
      <Header>
        <div>
          <Title>Articles Bank</Title>
          <Subtitle>
            Medical and legal research the firm uses to support claims. Browse by
            medical condition or toxic exposure, and by whether an article helps prove
            the link to service (nexus), the rating (evaluation), or both.
          </Subtitle>
        </div>
      </Header>

      <SearchPanel>
        <SearchRow
          onSubmit={(event) => {
            event.preventDefault();
            updateParams((next) => {
              if (queryInput.trim()) next.set("q", queryInput.trim());
              else next.delete("q");
            });
          }}
        >
          <Input
            size="large"
            prefix={<MagnifyingGlass size={18} />}
            value={queryInput}
            onChange={(event) => setQueryInput(event.target.value)}
            placeholder='Search article text, such as "burn pits"'
            allowClear
          />
          <Button type="primary" size="large" htmlType="submit">
            Search
          </Button>
        </SearchRow>

        <BrowsePanel>
          <BrowseHeading>
            <Segmented
              value={shelfTab}
              options={SHELF_TABS}
              onChange={(value) => setShelfTab(value)}
            />
            <ShelfNote>{SHELF_NOTES[shelfTab]}</ShelfNote>
          </BrowseHeading>
          <ShelfRow>
            {facetLoading && <Spin size="small" />}
            {shelfFacets.map((shelf) => (
              <Tag.CheckableTag
                key={shelf.value}
                checked={shelfSelected.includes(shelf.value)}
                onChange={(checked) => toggleShelf(shelfKey, shelf.value, checked)}
                style={{ border: "1px solid #d7deea", padding: "3px 10px", fontSize: 13 }}
              >
                {shelf.label} ({shelf.count})
              </Tag.CheckableTag>
            ))}
            {!facetLoading && shelfFacets.length === 0 && (
              <ShelfNote>Nothing on these shelves yet.</ShelfNote>
            )}
          </ShelfRow>
          {shelfTab === "condition" &&
            openSystemConditions.map((system) => (
              <div key={system.value}>
                <ShelfSubheading>Conditions in {system.label}</ShelfSubheading>
                <ShelfRow style={{ marginTop: 6 }}>
                  {system.conditions.map((condition) => (
                    <Tag.CheckableTag
                      key={condition.value}
                      checked={conditions.includes(condition.value)}
                      onChange={(checked) => toggleShelf("condition", condition.value, checked)}
                      style={{ border: "1px solid #c9d6ea", padding: "2px 8px", fontSize: 12 }}
                    >
                      {condition.label} ({condition.count})
                    </Tag.CheckableTag>
                  ))}
                  {system.hidden > 0 && !showAllConditions && (
                    <Button type="link" size="small" onClick={() => setShowAllConditions(true)}>
                      Show {system.hidden} more
                    </Button>
                  )}
                </ShelfRow>
              </div>
            ))}
          <PartRow>
            <ShelfSubheading>Part of the claim</ShelfSubheading>
            <Segmented
              value={claimParts[0] || "any"}
              options={partOptions}
              disabled={claimPartFacets.length === 0}
              onChange={(value) => setMany("claim_part", value === "any" ? [] : [value])}
            />
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              loading={facetLoading}
              value={conditions}
              placeholder="Find a specific condition"
              options={conditionOptions}
              onChange={(values) => setMany("condition", values)}
              style={{ minWidth: 260, flex: 1 }}
            />
          </PartRow>
        </BrowsePanel>
        <FilterFooter>
          <span style={{ color: "#66758a", fontSize: 12 }}>
            Choosing a condition and a part of the claim finds articles that support
            that condition for that part, such as sleep apnea and nexus.
          </span>
          <Button
            type="link"
            onClick={() => {
              setQueryInput("");
              setSearchParams(new URLSearchParams({ page: "1" }));
            }}
          >
            Clear filters
          </Button>
        </FilterFooter>
      </SearchPanel>

      {searchError && (
        <div role="alert" style={{ marginTop: 14, color: "#b42318" }}>
          {searchError}
        </div>
      )}

      <ResultSummary>
        {loading ? "Searching…" : `${total.toLocaleString()} articles found`}
      </ResultSummary>

      {loading ? (
        <EmptyState style={{ textAlign: "center" }}>
          <Spin />
        </EmptyState>
      ) : entries.length === 0 ? (
        <EmptyState>
          <Empty description="No articles match these filters." />
        </EmptyState>
      ) : (
        <ResultList>
          {entries.map((entry) => (
            <ResultCard
              key={entry.id}
              role="button"
              tabIndex={0}
              onClick={() => openDetail(entry)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openDetail(entry);
                }
              }}
            >
              <ResultTop>
                <div>
                  <ResultTitle>{entry.title}</ResultTitle>
                  <Meta>
                    <span>{entry.article_kind_label}</span>
                    {entry.year && <span>{entry.year}</span>}
                    {entry.publication && <span>{entry.publication}</span>}
                  </Meta>
                </div>
                {entry.strength && (
                  <Tag color={STRENGTH_COLORS[entry.strength] || "default"}>
                    {capitalize(entry.strength)}
                  </Tag>
                )}
              </ResultTop>
              {entry.snippet ? (
                <Snippet>{highlightedSnippet(entry.snippet)}</Snippet>
              ) : (
                entry.summary && <Snippet>{entry.summary}</Snippet>
              )}
              <TagRow>
                {PART_TAGS[entry.claim_part] && (
                  <Tag color={PART_COLORS[entry.claim_part]}>{PART_TAGS[entry.claim_part]}</Tag>
                )}
                {(entry.exposures || []).map((exposure) => (
                  <Tag color="volcano" key={exposure}>
                    {exposure}
                  </Tag>
                ))}
                {(entry.conditions || []).slice(0, VISIBLE_CONDITIONS).map((condition) => (
                  <Tag color="blue" key={condition}>
                    {condition}
                  </Tag>
                ))}
                {(entry.conditions || []).length > VISIBLE_CONDITIONS && (
                  <Tag>+{entry.conditions.length - VISIBLE_CONDITIONS}</Tag>
                )}
              </TagRow>
            </ResultCard>
          ))}
        </ResultList>
      )}

      {!loading && total > PAGE_SIZE && (
        <PaginationRow>
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            showSizeChanger={false}
            onChange={(nextPage) => {
              const next = new URLSearchParams(searchParams);
              next.set("page", String(nextPage));
              setSearchParams(next);
            }}
          />
        </PaginationRow>
      )}

      <Drawer
        title={selected?.title || "Article"}
        width={canPreview ? "min(1500px, calc(100vw - 24px))" : 520}
        open={Boolean(selected)}
        onClose={closeDetail}
      >
        {detailLoading ? (
          <Spin />
        ) : (
          selected && (
            <DetailLayout $withPreview={canPreview}>
              <DetailInfo>
                <DetailSection>
                  <DetailHeading>Article</DetailHeading>
                  <DetailValue>
                    {[selected.authors, selected.publication, selected.year]
                      .filter(Boolean)
                      .join(" · ")}
                    {selected.summary && (
                      <p style={{ margin: "8px 0 0" }}>{selected.summary}</p>
                    )}
                  </DetailValue>
                </DetailSection>
                <DetailSection>
                  <DetailHeading>How it helps a claim</DetailHeading>
                  {(selected.claim_support || []).length === 0 && (
                    <DetailValue>No specific claim support recorded.</DetailValue>
                  )}
                  {(selected.claim_support || []).map((item, index) => (
                    <SupportItem key={`${item.element}-${index}`}>
                      <SupportLabel>{item.element_label}</SupportLabel>
                      {(item.conditions || []).length > 0 && (
                        <TagRow style={{ marginTop: 6 }}>
                          {item.conditions.map((condition) => (
                            <Tag color="blue" key={condition}>
                              {condition}
                            </Tag>
                          ))}
                        </TagRow>
                      )}
                      {item.explanation && (
                        <SupportExplanation>{item.explanation}</SupportExplanation>
                      )}
                      {item.quote && (
                        <Quote>
                          “{item.quote}”
                          {item.page && <QuotePage>p. {item.page}</QuotePage>}
                        </Quote>
                      )}
                    </SupportItem>
                  ))}
                </DetailSection>
                {(selected.exposures_events || []).length > 0 && (
                  <DetailSection>
                    <DetailHeading>Exposures and events</DetailHeading>
                    <TagRow>
                      {selected.exposures_events.map((value) => (
                        <Tag key={value}>{value}</Tag>
                      ))}
                    </TagRow>
                  </DetailSection>
                )}
                <DetailSection>
                  <DetailHeading>File</DetailHeading>
                  <DetailValue>{selected.source_name}</DetailValue>
                </DetailSection>
              </DetailInfo>
              {canPreview && (
                <InlinePreviewPanel>
                  <PreviewBody $embedded>
                    <ReadOnlyDocumentViewer
                      key={selected.source_document_id}
                      documentId={selected.source_document_id}
                      filename={selected.source_name}
                      fileType={sourceType}
                    />
                  </PreviewBody>
                </InlinePreviewPanel>
              )}
            </DetailLayout>
          )
        )}
      </Drawer>
    </Page>
  );
};

export default ArticlesBank;
